import * as React from "react";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { useToasts } from "react-toast-notifications";
import { DepartmentCache } from "../../../../../store/cache/requested_service";
import { ClinicianServiceSelection } from "../../../../../store/department";
import store from "../../../../../store";
import { TRequestedService } from "intake_shared/src/types";
import styles from "./ManageClinicianServices.module.scss";
import { Button } from "../../../bits/Button/Button";
import { FormCheckbox } from "../../../bits/FormCheckbox/FormCheckbox";
import { AlertDialog } from "../../../bits/AlertDialog/AlertDialog";

export default function ManageClinicianServices() {
    const olc = React.useContext(CacheContext);
    const { addToast } = useToasts();
    const allAvailableServices = olc?.requestedServices || {};
    const allAvailableDepartments = olc?.departments || ({} as DepartmentCache);
    const [showDialog, setShowDialog] = React.useState(false);
    const [allClinicianIdentifers, setAllClinicianIdentifiers] = React.useState<
        Array<{ id: string; first_name: string; last_name: string }>
    >([]);
    const [allClinicianServiceSelections, setAllClinicianServiceSelections] =
        React.useState<ClinicianServiceSelection>({});

    const fetchAllClinicianServiceSelection = async () => {
        setAllClinicianServiceSelections(
            await store.fetchAllCliniciansServiceSelection()
        );
    };

    React.useEffect(() => {
        const fetchAllClinicianIdentifiers = async () => {
            const identifiers = await store.fetchAllCliniciansIdentifier();
            setAllClinicianIdentifiers(identifiers);
        };

        fetchAllClinicianServiceSelection();
        fetchAllClinicianIdentifiers();
    }, []);

    const [selectedDepts, setSelectedDepts] = React.useState<TRequestedService>({});
    const [selectedServices, setSelectedServices] =
        React.useState<TRequestedService>({});
    const [selectedClinicians, setSelectedClinicians] = React.useState<string[]>([]);

    async function saveServiceMapping() {
        const toSave = selectedClinicians.reduce(
            (acc, clinicianId) => {
                acc[clinicianId] = {
                    departments: Object.keys(selectedDepts),
                    services: Object.keys(selectedServices),
                };
                return acc;
            },
            {} as {
                [clinicianId: string]: {
                    departments: string[];
                    services: string[];
                };
            }
        );
        try {
            await store.updateClinicianServiceSelection(toSave);
            addToast("Selection saved", {
                appearance: "success",
                autoDismiss: true,
            });
            resetSelections();
            await fetchAllClinicianServiceSelection();
            setShowDialog(false);
        } catch (e) {
            addToast(`Error saving selection. ${e.message}`, {
                appearance: "error",
                autoDismiss: false,
            });
        }
    }

    function resetSelections() {
        setSelectedClinicians([]);
        setSelectedDepts({});
        setSelectedServices({});
    }
    return (
        <div>
            <AlertDialog
                open={showDialog}
                onClose={() => setShowDialog(false)}
                title="Update Clinician Services?"
                description={`Update the department and service selection for ${selectedClinicians.length} clinicians? This will override existing selections and will affect which services the selected clinicians can choose when listing availability`}
                onSaveClick={saveServiceMapping}
                onCancelClick={() => setShowDialog(false)}
            />
            <div className={styles.content}>
                <div className={styles.contentCard}>
                    <div className={styles.contentHeader}>
                        <div className={styles.cardTitle}>
                            Clinicians
                            <span className={styles.contentCount}>
                                {selectedClinicians.length}
                            </span>
                        </div>
                        <div className={styles.cardButtons}>
                            <Button
                                variant="normal"
                                className={styles.cardButton}
                                onClick={() => {
                                    const allClinciansId =
                                        allClinicianIdentifers.map(({ id }) => id);
                                    setSelectedClinicians(allClinciansId);
                                }}
                            >
                                Select all
                            </Button>
                            <Button
                                variant="normal"
                                className={styles.cardButton}
                                onClick={() => {
                                    setSelectedClinicians([]);
                                }}
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                    <div className={styles.contentBody}>
                        {allClinicianIdentifers.map((clinician) => {
                            return (
                                <div
                                    key={clinician.id}
                                    className={styles.clinicianListItem}
                                >
                                    <FormCheckbox
                                        label={`${clinician.first_name} ${clinician.last_name}`}
                                        checked={selectedClinicians.includes(
                                            clinician.id
                                        )}
                                        onChange={(e) => {
                                            const newSelectedClinicians = [
                                                ...selectedClinicians,
                                            ];
                                            if (e.target.checked) {
                                                newSelectedClinicians.push(
                                                    clinician.id
                                                );
                                            } else {
                                                newSelectedClinicians.splice(
                                                    newSelectedClinicians.indexOf(
                                                        clinician.id
                                                    ),
                                                    1
                                                );
                                            }
                                            setSelectedClinicians(
                                                newSelectedClinicians
                                            );
                                        }}
                                        noBorder
                                        labelClass={styles.clinicianLabel}
                                    />
                                    <div className={styles.clinicianDetails}>
                                        {
                                            allClinicianServiceSelections[
                                                clinician.id
                                            ]?.departments.length
                                        }{" "}
                                        Departments -{" "}
                                        {allClinicianServiceSelections[
                                            clinician.id
                                        ]?.departments.join(", ")}
                                        <br></br>
                                        {
                                            allClinicianServiceSelections[
                                                clinician.id
                                            ]?.services.length
                                        }{" "}
                                        Services -{" "}
                                        {allClinicianServiceSelections[
                                            clinician.id
                                        ]?.services
                                            .map(
                                                (service) =>
                                                    allAvailableServices[service]
                                            )
                                            .join(", ")}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className={styles.contentCard}>
                    <div className={styles.contentHeader}>
                        <div className={styles.cardTitle}>
                            Departments
                            <span className={styles.contentCount}>
                                {Object.keys(selectedDepts).length}
                            </span>
                        </div>
                        <div className={styles.cardButtons}>
                            <Button
                                variant="normal"
                                onClick={() => {
                                    const newSelectedDepts = {
                                        ...selectedDepts,
                                    };
                                    Object.entries(allAvailableDepartments).forEach(
                                        ([departments, details]) => {
                                            newSelectedDepts[departments] =
                                                details.label as string;
                                        }
                                    );
                                    setSelectedDepts(newSelectedDepts);
                                }}
                                className={styles.cardButton}
                            >
                                Select all
                            </Button>
                            <Button
                                variant="normal"
                                onClick={() => {
                                    setSelectedDepts({});
                                }}
                                className={styles.cardButton}
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                    <div className={styles.contentBody}>
                        {Object.entries(allAvailableDepartments).map(
                            ([departments, details], idx) => {
                                return (
                                    <div
                                        key={`${departments}-${idx}`}
                                        className={styles.departmentListItem}
                                    >
                                        <FormCheckbox
                                            label={details.label}
                                            checked={departments in selectedDepts}
                                            onChange={(e) => {
                                                const newSelectedDepts = {
                                                    ...selectedDepts,
                                                };
                                                if (e.target.checked) {
                                                    newSelectedDepts[departments] =
                                                        details.label as string;
                                                } else {
                                                    delete newSelectedDepts[
                                                        departments
                                                    ];
                                                }
                                                setSelectedDepts(newSelectedDepts);
                                            }}
                                            noBorder
                                            labelClass={styles.departmentLabel}
                                        />
                                    </div>
                                );
                            }
                        )}
                    </div>
                </div>
                <div className={styles.contentCard}>
                    <div className={styles.contentHeader}>
                        <div className={styles.cardTitle}>
                            Services
                            <span className={styles.contentCount}>
                                {Object.keys(selectedServices).length}
                            </span>
                        </div>
                        <div className={styles.cardButtons}>
                            <Button
                                variant="normal"
                                onClick={() => {
                                    const newSelectedServices = {
                                        ...selectedServices,
                                    };
                                    Object.entries(allAvailableServices).forEach(
                                        ([service, label]) => {
                                            newSelectedServices[service] = label;
                                        }
                                    );
                                    setSelectedServices(newSelectedServices);
                                }}
                                className={styles.cardButton}
                            >
                                Select all
                            </Button>
                            <Button
                                variant="normal"
                                onClick={() => {
                                    setSelectedServices({});
                                }}
                                className={styles.cardButton}
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                    <div className={styles.contentBody}>
                        {Object.entries(allAvailableServices).map(
                            ([service, serviceLabel], idx) => {
                                return (
                                    <div
                                        key={`${service}-${idx}`}
                                        className={styles.departmentListItem}
                                    >
                                        <FormCheckbox
                                            label={serviceLabel}
                                            checked={service in selectedServices}
                                            onChange={(e) => {
                                                const newSelectedServices = {
                                                    ...selectedServices,
                                                };
                                                if (e.target.checked) {
                                                    newSelectedServices[service] =
                                                        serviceLabel;
                                                } else {
                                                    delete newSelectedServices[
                                                        service
                                                    ];
                                                }
                                                setSelectedServices(
                                                    newSelectedServices
                                                );
                                            }}
                                            noBorder
                                            labelClass={styles.departmentLabel}
                                        />
                                    </div>
                                );
                            }
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <div className={styles.footerItem}>
                    Mapping {selectedClinicians.length} clinician(s) →{" "}
                    {Object.keys(selectedDepts).length} department(s) →{" "}
                    {Object.keys(selectedServices).length} service(s)
                </div>
                <div className={styles.footerButtons}>
                    <Button onClick={() => setShowDialog(true)}>
                        Update clinician mapping
                    </Button>
                    <Button variant="secondary" onClick={resetSelections}>
                        Reset
                    </Button>
                </div>
            </div>
        </div>
    );
}
